import axios from "axios";
import { useState } from "react";
import { Calendar, TrendingUp, Search, X, Loader2, Wallet, Receipt, Pencil } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Today in Iraq (UTC+3) for the default datepicker value.
const iraqToday = () => {
  const now = new Date();
  const iraq = new Date(now.getTime() + (3 * 3600 * 1000) - now.getTimezoneOffset() * 60000);
  return iraq.toISOString().slice(0, 10);
};

// Quick presets — last N business days back from today.
const presetDates = [
  { label: "أمس", offset: 1 },
  { label: "آخر 3 أيام", offset: 3, range: true },
  { label: "آخر 7 أيام", offset: 7, range: true },
  { label: "آخر 30 يوم", offset: 30, range: true },
  { label: "الكل", offset: null, all: true },
];

// Subtract `n` days from an ISO YYYY-MM-DD string.
const shiftDate = (iso, days) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

/**
 * Bar with a "🔎 بحث بالتاريخ" launcher. Opens an inline panel where the
 * admin can pick a specific Iraqi business day or a date range, and see the
 * sold / received / profit / cartons numbers for that window. The numbers
 * on the "today" cards stay untouched — this is purely a query tool, so
 * historical data is never lost from sight.
 */
const HistoricalStatsBar = () => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(iraqToday());
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRange = async (params) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/stats-range`, { params });
      setData(res);
    } finally { setLoading(false); }
  };

  const handleSearch = () => {
    if (dateTo) {
      // Date range mode (uses calendar dates, not business-day windows)
      fetchRange({
        date_from: `${date}T00:00:00`,
        date_to: `${dateTo}T23:59:59`,
      });
    } else {
      // Single business day mode
      fetchRange({ iraq_date: date });
    }
  };

  const handlePreset = (offset, isRange, isAll) => {
    if (isAll) {
      // Lifetime — use a very old start date so the range covers everything.
      setDate("2020-01-01");
      setDateTo(iraqToday());
      fetchRange({ date_from: "2020-01-01T00:00:00", date_to: `${iraqToday()}T23:59:59` });
      return;
    }
    const start = shiftDate(iraqToday(), offset);
    if (isRange) {
      setDate(start);
      setDateTo(iraqToday());
      fetchRange({ date_from: `${start}T00:00:00`, date_to: `${iraqToday()}T23:59:59` });
    } else {
      setDate(start);
      setDateTo("");
      fetchRange({ iraq_date: start });
    }
  };

  return (
    <div className="bg-white border border-[#E4EBE6] rounded-2xl overflow-hidden shadow-sm" data-testid="historical-stats-bar">
      {/* Launcher */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="historical-stats-toggle"
        className="w-full px-4 py-3 flex items-center justify-between gap-2 hover:bg-[#F3F6F4] transition-colors"
      >
        <div className="flex items-center gap-2 text-[#1E3A5F]">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] text-white flex items-center justify-center">
            <Search className="w-4 h-4" />
          </div>
          <div className="text-right">
            <p className="font-black text-sm">بحث في الأرباح بتاريخ آخر</p>
            <p className="text-[10px] text-[#5C6E64]">شوف مبيعاتك وأرباحك بأي يوم أو فترة (البيانات محفوظة دائماً)</p>
          </div>
        </div>
        <Calendar className={`w-5 h-5 text-[#5C6E64] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[#E4EBE6] p-4 space-y-3 bg-[#FAFCFB]">
          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            {presetDates.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => handlePreset(p.offset, !!p.range, !!p.all)}
                data-testid={`historical-preset-${p.all ? 'all' : p.offset}`}
                className="bg-white border border-[#E4EBE6] hover:bg-[#1E3A5F] hover:text-white px-3 py-1.5 rounded-full text-xs font-bold transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Date inputs */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[#5C6E64] block mb-1">من تاريخ</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="historical-date-from"
                className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#5C6E64] block mb-1">إلى تاريخ (اختياري)</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="historical-date-to"
                placeholder="(يوم واحد فقط)"
                className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] text-sm"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading}
            data-testid="historical-search-btn"
            className="w-full bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white font-bold py-2.5 rounded-2xl flex items-center justify-center gap-2 shadow"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "جاري البحث..." : "بحث"}
          </button>

          {/* Results */}
          {data && (
            <div className="bg-white rounded-2xl border-2 border-[#3B82F6] p-3 mt-3 relative" data-testid="historical-result">
              <button
                type="button"
                onClick={() => setData(null)}
                className="absolute top-2 left-2 w-7 h-7 rounded-full bg-[#F3F6F4] hover:bg-[#E4EBE6] flex items-center justify-center text-[#5C6E64]"
                aria-label="إغلاق"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <p className="text-xs text-[#5C6E64] font-bold mb-2">{data.label}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Stat label="المبلغ المباع" value={`${fmt(data.sold)} د.ع`} color="#15803D" kind="sold" current={data.sold} onAdjusted={handleSearch} />
                <Stat label="ربح صافي" value={`${fmt(data.profit)} د.ع`} color="#1E3A5F" icon={TrendingUp} kind="profit" current={data.profit} onAdjusted={handleSearch} />
                <Stat label="الكراتين" value={fmt(data.cartons)} color="#F5A623" />
                <Stat label="عدد الطلبات" value={fmt(data.orders_count)} color="#7C3AED" />
                <Stat label="الصرفيات الكلية" value={`${fmt(data.total_expenses)} د.ع`} color="#E85D45" icon={Receipt} />
                <Stat label="النقد بالصندوق" value={`${fmt(data.cash_in_box)} د.ع`} color="#0EA5E9" icon={Wallet} kind="cash_box" current={data.cash_in_box} onAdjusted={handleSearch} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, icon: Icon, kind, current, onAdjusted }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="rounded-xl p-2 text-right relative group" style={{ background: `${color}10`, border: `1px solid ${color}40` }}>
      <div className="flex items-center justify-between gap-1">
        <p className="text-[10px] text-[#5C6E64] truncate">{label}</p>
        <div className="flex items-center gap-1">
          {kind && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid={`stat-edit-${kind}`}
              title={`تعديل يدوي على ${label}`}
              className="w-5 h-5 rounded-full bg-white hover:bg-[#1E3A5F] hover:text-white text-[#5C6E64] flex items-center justify-center transition-colors shrink-0"
            >
              <Pencil className="w-2.5 h-2.5" />
            </button>
          )}
          {Icon && <Icon className="w-3 h-3" style={{ color }} />}
        </div>
      </div>
      <p className="font-black text-sm leading-tight" dir="ltr" style={{ color }}>{value}</p>
      {editing && (
        <ManualAdjustModal
          kind={kind}
          label={label}
          color={color}
          currentValue={Number(current || 0)}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onAdjusted && onAdjusted(); }}
        />
      )}
    </div>
  );
};

// Inline modal for adjusting a stat card. Two modes:
//  1) "adjust" — add (+) / subtract (−) a delta. Stored verbatim.
//  2) "set"    — type the new TOTAL value. Stored delta = newValue − currentValue.
// Either way, the entry is purely additive on top of computed values — it
// NEVER touches debt, orders, or stock.
const ManualAdjustModal = ({ kind, label, color, currentValue = 0, onClose, onSaved }) => {
  const [mode, setMode] = useState("set"); // default = "set the total"
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    const n = Number(amount);
    if (!isFinite(n)) {
      toast.error("أدخل قيمة صحيحة");
      return;
    }
    let delta = n;
    let prettyReason = reason;
    if (mode === "set") {
      delta = n - Number(currentValue || 0);
      if (delta === 0) {
        toast.error("القيمة الجديدة مطابقة للحالية");
        return;
      }
      prettyReason = reason || `تعيين القيمة إلى ${n.toLocaleString()}`;
    } else if (delta === 0) {
      toast.error("أدخل قيمة موجبة (+) أو سالبة (−)");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/manual-finance-adjustment`, { kind, amount: delta, reason: prettyReason });
      toast.success(`تم: ${delta > 0 ? "+" : ""}${delta.toLocaleString()} د.ع على ${label}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل التعديل");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[160] bg-black/60 flex items-center justify-center p-3" dir="rtl" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid={`manual-adjust-modal-${kind}`}>
        <div className="flex items-center justify-between">
          <p className="font-black text-[#1E3A5F]">تعديل يدوي: <span style={{ color }}>{label}</span></p>
          <button type="button" onClick={onClose} className="w-7 h-7 rounded-full bg-[#F3F6F4] flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Current value summary */}
        <div className="rounded-xl p-2.5 flex items-center justify-between" style={{ background: `${color}10`, border: `1px solid ${color}40` }}>
          <span className="text-[11px] text-[#5C6E64] font-bold">المبلغ الحالي</span>
          <span className="font-black text-base" dir="ltr" style={{ color }}>{Number(currentValue || 0).toLocaleString()} د.ع</span>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-1 bg-[#F3F6F4] rounded-xl p-1">
          <button
            type="button"
            onClick={() => { setMode("set"); setAmount(""); }}
            data-testid={`adjust-mode-set-${kind}`}
            className={`py-1.5 rounded-lg text-xs font-bold transition-colors ${mode === "set" ? "bg-white text-[#1E3A5F] shadow" : "text-[#5C6E64]"}`}
          >
            تعيين قيمة جديدة
          </button>
          <button
            type="button"
            onClick={() => { setMode("adjust"); setAmount(""); }}
            data-testid={`adjust-mode-adjust-${kind}`}
            className={`py-1.5 rounded-lg text-xs font-bold transition-colors ${mode === "adjust" ? "bg-white text-[#1E3A5F] shadow" : "text-[#5C6E64]"}`}
          >
            إضافة / خصم
          </button>
        </div>

        <p className="text-[11px] text-[#5C6E64] bg-[#FEF3C7] rounded-lg p-2 leading-tight">
          {mode === "set"
            ? <>اكتب <b>القيمة الكلية الجديدة</b>. النظام يحتسب الفرق ويسجله كتعديل يدوي (لا يمس الديون أو المبيعات أو المخزون).</>
            : <>⚠️ التعديل <b>مستقل تماماً</b> — موجب (+) لإضافة، سالب (−) لخصم. لا يمس الديون، المبيعات، أو المخزون.</>}
        </p>

        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={mode === "set" ? `القيمة الجديدة (مثلاً ${Number(currentValue || 0).toLocaleString()})` : "موجب لإضافة (+) أو سالب لخصم (−)"}
          dir="ltr"
          data-testid={`manual-adjust-amount-${kind}`}
          autoFocus
          className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
        />

        {/* Preview delta in "set" mode */}
        {mode === "set" && amount !== "" && isFinite(Number(amount)) && (
          <p className="text-[11px] font-bold text-center" style={{ color: (Number(amount) - Number(currentValue || 0)) >= 0 ? "#15803D" : "#DC2626" }}>
            الفرق: {(Number(amount) - Number(currentValue || 0)) >= 0 ? "+" : ""}{(Number(amount) - Number(currentValue || 0)).toLocaleString()} د.ع
          </p>
        )}

        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="سبب التعديل (اختياري)"
          data-testid={`manual-adjust-reason-${kind}`}
          className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] text-sm text-right"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || amount === ""}
            data-testid={`manual-adjust-save-${kind}`}
            className="flex-1 bg-[#1E3A5F] hover:bg-[#152a47] disabled:opacity-50 text-white font-bold py-2 rounded-xl"
          >
            {busy ? "جاري الحفظ..." : "حفظ"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-[#F3F6F4] text-[#5C6E64] font-bold px-4 rounded-xl"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoricalStatsBar;
